import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { AmplifyGet } from '@sunrun/sales-experience-shared/amplify-api-wrapper';
import { getEnvironment } from '@sunrun/sales-experience-shared';
import { isNumericEmployeeId } from 'helpers/employeeId';
import { getNewDashboardOrgType } from 'helpers/repTypeHelper';
import { logger } from 'providers/logger';
import { useUserProfile } from 'providers/UserProfile';
import type {
  LeaderboardData,
  PerformanceData,
  PerformanceProfileData,
} from 'types/performance';

type PerformanceProfileResponse = {
  statusCode: number;
  body: PerformanceProfileData;
};

const useRepProfileData = () => {
  const [searchParams] = useSearchParams();
  const { userProfile } = useUserProfile();
  const jwt = searchParams.get('jwt');
  const emailFromParams = searchParams.get('email');
  const employeeIdFromParams = searchParams.get('employeeId');

  const orgType = getNewDashboardOrgType(userProfile);
  const isScorecardProfileOrg =
    orgType === 'STREET' || orgType === 'BLOCK' || orgType === 'CHAIN';

  const shouldFetch =
    isScorecardProfileOrg &&
    jwt &&
    emailFromParams &&
    isNumericEmployeeId(employeeIdFromParams);

  const { data, isLoading, error } = useSWR<PerformanceProfileResponse | null>(
    shouldFetch
      ? ['/performanceProfile', jwt, emailFromParams, employeeIdFromParams]
      : null,
    async () => {
      if (
        !jwt ||
        !emailFromParams ||
        !isNumericEmployeeId(employeeIdFromParams)
      )
        return null;
      return AmplifyGet<PerformanceProfileResponse>(
        'Get rep profile data',
        'SalesExpDashboardApi',
        '/performanceProfile',
        {
          queryStringParameters: {
            jwt,
            email: emailFromParams,
            employee_id: employeeIdFromParams,
          },
        },
        logger,
        {
          expectedErrorCodes: [400, 401, 403, 404],
        },
      );
    },
  );

  return {
    profileData: data?.body ?? null,
    isLoading,
    error,
  };
};

type DashboardProfilePhotoResponse = {
  statusCode: number;
  body: unknown;
};

const useDashboardProfilePhoto = () => {
  const [searchParams] = useSearchParams();
  const environment = getEnvironment();
  const employeeIdFromParams =
    environment === 'production' ? searchParams.get('employeeId') : '516806';

  const { data, isLoading, error } =
    useSWR<DashboardProfilePhotoResponse | null>(
      ['/dashboardProfilePhoto', employeeIdFromParams],
      async () => {
        if (!employeeIdFromParams) return null;
        return AmplifyGet<DashboardProfilePhotoResponse>(
          'Get dashboard profile photo',
          'SalesExpDashboardApi',
          '/dashboardProfilePhoto',
          {
            queryStringParameters: {
              employee_id: employeeIdFromParams,
            },
          },
          logger,
          {
            expectedErrorCodes: [401, 403],
          },
        );
      },
    );

  let profilePhotoUrl = null;
  if (data?.body) {
    // @ts-ignore
    profilePhotoUrl = data?.body?.[employeeIdFromParams] ?? null;
  }

  return { profilePhotoUrl, isLoading, error };
};

type PerformanceDataResponse = {
  statusCode: number;
  body: PerformanceData;
};

const usePerformanceData = () => {
  const [searchParams] = useSearchParams();
  const jwt = searchParams.get('jwt');
  const emailFromParams = searchParams.get('email');
  const employeeIdFromParams = searchParams.get('employeeId');

  const shouldFetch =
    jwt && emailFromParams && isNumericEmployeeId(employeeIdFromParams);

  const { data, isLoading, error } = useSWR<PerformanceDataResponse | null>(
    shouldFetch
      ? ['/performanceData', jwt, emailFromParams, employeeIdFromParams]
      : null,
    async () => {
      if (
        !jwt ||
        !emailFromParams ||
        !isNumericEmployeeId(employeeIdFromParams)
      ) {
        return null;
      }
      return AmplifyGet<PerformanceDataResponse>(
        'Get performance data',
        'SalesExpDashboardApi',
        '/performanceData',
        {
          queryStringParameters: {
            jwt,
            email: emailFromParams,
            employee_id: employeeIdFromParams,
          },
        },
        logger,
        {
          expectedErrorCodes: [400, 401, 403],
        },
      );
    },
  );

  return {
    performanceData: data?.body ?? null,
    isPerformanceDataLoading: isLoading,
    performanceDataError: error,
  };
};

type LeaderboardDataResponse = {
  statusCode: number;
  body: LeaderboardData;
};

type LeaderboardIdentity = {
  email: string;
  employeeId: string;
};

type LeaderboardDateRange = {
  startDate: string;
  endDate: string;
};

// `identityOverride` lets a VIP viewer load another rep's leaderboard.
const useLeaderboardData = (
  identityOverride?: LeaderboardIdentity,
  dateRange?: LeaderboardDateRange,
) => {
  const [searchParams] = useSearchParams();
  const jwt = searchParams.get('jwt');
  const emailFromParams = searchParams.get('email');
  const employeeIdFromParams = searchParams.get('employeeId');

  const email = identityOverride?.email ?? emailFromParams;
  const employeeId = identityOverride?.employeeId ?? employeeIdFromParams;
  const startDate = dateRange?.startDate;
  const endDate = dateRange?.endDate;

  const { data, isLoading, error } = useSWR<LeaderboardDataResponse | null>(
    ['/performanceLeaderboard', jwt, email, employeeId, startDate, endDate],
    async () => {
      if (!jwt || !email || !employeeId) return null;
      return AmplifyGet<LeaderboardDataResponse>(
        'Get leaderboard data',
        'SalesExpDashboardApi',
        '/performanceLeaderboard',
        {
          queryStringParameters: {
            jwt,
            email,
            employee_id: employeeId,
            ...(startDate && endDate
              ? { start_date: startDate, end_date: endDate }
              : {}),
          },
        },
        logger,
        {
          expectedErrorCodes: [401, 403],
        },
      );
    },
  );

  return {
    leaderboardData: data?.body ?? null,
    isLeaderboardDataLoading: isLoading,
    leaderboardDataError: error,
  };
};

export type { LeaderboardDateRange, LeaderboardIdentity };
export {
  usePerformanceData,
  useRepProfileData,
  useDashboardProfilePhoto,
  useLeaderboardData,
};
